import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_COLOR } from '@/shared/content/garments';
import { PARTNER_SLUG } from '@/shared/lib/partner';

/**
 * Что человек выбрал: артист, изделие, цвет и количество по размерам.
 *
 * Вынесено из App в стор ради одного свойства — сохранения между
 * перезагрузками. Раньше выбор жил в состоянии компонента и умирал
 * вместе со страницей: человек обновлял вкладку и возвращался к первому
 * экрану с пустыми руками, хотя шаг и раскладка принтов уже сохранялись.
 *
 * Изделие хранится идентификатором, а не объектом: каталог приезжает
 * с сервера и может измениться, а хранить его копию в браузере значит
 * когда-нибудь показать цену, которой уже нет.
 */
interface SelectionState {
  artist: string | null;
  productId: string | null;
  color: string;
  quantities: Record<string, number>;

  setArtist: (artist: string | null) => void;
  setProductId: (id: string | null) => void;
  setColor: (color: string) => void;
  setQuantity: (size: string, qty: number) => void;
  /** Новое изделие: свой цвет, размеры заново. Чужие сюда не переносятся. */
  resetFor: (color: string) => void;
  /**
   * Позиция ушла в корзину — черновик выбора пуст.
   *
   * Без этого количества продолжали жить после заказа: человек собирал
   * вторую вещь и видел уже выбранные размеры, которых не выбирал.
   */
  clearQuantities: () => void;
}

export const useSelectionStore = create<SelectionState>()(
  persist(
    (set) => ({
      artist: null,
      productId: null,
      color: DEFAULT_COLOR,
      quantities: {},

      setArtist: (artist) => set({ artist }),
      setProductId: (productId) => set({ productId }),
      setColor: (color) => set({ color }),

      setQuantity: (size, qty) =>
        set((state) => ({
          quantities: { ...state.quantities, [size]: Math.max(0, qty) },
        })),

      // Размеры пустые, а не «первый по умолчанию»: человек не выбирал
      // ни одного, и подставленная за него единица уезжала в корзину
      // незамеченной.
      resetFor: (color) => set({ color, quantities: {} }),

      clearQuantities: () => set({ quantities: {} }),
    }),
    // Ключ привязан к заказчику: у другого свой каталог, свои цвета
    // и свои размеры, и чужой сохранённый выбор там ничего не значит.
    { name: `ls-selection:${PARTNER_SLUG}` },
  ),
);
